/**
 * Combination Engine
 * ──────────────────
 * Pure functions that turn parameter assignments into trackable combinations
 * and fold new executions into existing records. No I/O — the storage layer
 * persists whatever these return, which keeps the logic trivially testable.
 */

import type {
  ActionType,
  Combination,
  ParameterAssignment,
} from "@/types";
import { buildSignature, hashCombination } from "@/lib/utils/id";

const EMPTY_ACTION_COUNTS: Record<ActionType, number> = {
  Manual_Go: 0,
  Top10_Click: 0,
  Favorite_Click: 0,
  Recent_Click: 0,
  History_Click: 0,
  Recommendation_Click: 0,
};

export interface ExecutionContext {
  userName: string;
  workbookName: string;
  dashboardName: string;
  actionType: ActionType;
  /** Injectable clock for deterministic tests. */
  now?: () => string;
}

function nowIso(ctx: ExecutionContext): string {
  return ctx.now ? ctx.now() : new Date().toISOString();
}

/**
 * Build a fresh combination from assignments. Used the first time a unique
 * combination is executed.
 */
export function createCombination(
  assignments: ParameterAssignment[],
  ctx: ExecutionContext,
): Combination {
  const ts = nowIso(ctx);
  const id = hashCombination(assignments);
  return {
    id,
    assignments,
    signature: buildSignature(assignments),
    usageCount: 1,
    actionCounts: { ...EMPTY_ACTION_COUNTS, [ctx.actionType]: 1 },
    uniqueUsers: [ctx.userName],
    createdAt: ts,
    createdBy: ctx.userName,
    lastUsedAt: ts,
    lastUsedBy: ctx.userName,
    workbookName: ctx.workbookName,
    dashboardName: ctx.dashboardName,
  };
}

/**
 * Fold a new execution into an existing combination, incrementing usage and
 * action-specific counters and tracking unique users. Returns a new object
 * (does not mutate the input) so it plays nicely with React/Zustand.
 */
export function recordExecution(
  existing: Combination,
  ctx: ExecutionContext,
): Combination {
  const ts = nowIso(ctx);
  const uniqueUsers = existing.uniqueUsers.includes(ctx.userName)
    ? existing.uniqueUsers
    : [...existing.uniqueUsers, ctx.userName];

  return {
    ...existing,
    usageCount: existing.usageCount + 1,
    actionCounts: {
      ...EMPTY_ACTION_COUNTS,
      ...existing.actionCounts,
      [ctx.actionType]: (existing.actionCounts[ctx.actionType] ?? 0) + 1,
    },
    uniqueUsers,
    lastUsedAt: ts,
    lastUsedBy: ctx.userName,
  };
}

/**
 * Upsert helper: given the current list and a new execution, return the updated
 * combination plus whether it was newly created. Callers persist the result.
 */
export function applyExecution(
  combinations: Combination[],
  assignments: ParameterAssignment[],
  ctx: ExecutionContext,
): { combination: Combination; isNew: boolean } {
  const id = hashCombination(assignments);
  const existing = combinations.find((c) => c.id === id);
  if (existing) {
    return { combination: recordExecution(existing, ctx), isNew: false };
  }
  return { combination: createCombination(assignments, ctx), isNew: true };
}
